<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { add, getDaysInMonth, lastDayOfMonth } from 'date-fns'
import { useI18n } from 'vue-i18n'

import { useMutation } from '@/composables/api'
import { useDialogsStore } from '@/stores/dialogsStore'
import { formatCurrency, formatDate } from '@/utils/format'
import type { components } from '@/types/_api'
import type { SettlementDocument } from '@/types/settlement-documents'
import type { AmortizationCommitBody } from './types'

import mdiMinus from '~icons/mdi/minus'
import mdiPlus from '~icons/mdi/plus'

import AmortizationBackToolbar from './AmortizationBackToolbar.vue'
import AmortizationSumRow from './AmortizationSumRow.vue'
import AmortizationTextRow from './AmortizationTextRow.vue'

// Setup window of the amortization dialog (ADA-4437 etapp 5). Ported from @old
// AmortizationSetup — the plan slider, the cost preview (details-new-amortization POST per
// payload change) and the first-payment-date / max-advices formulas are ported EXACTLY (the
// API counts that way). The fee/cost breakdown from the preview
// response renders directly in the "Hur har vi räknat?" section (ADA-4472 B — previously
// ADA-4433's fee card plus an expander).
type AmortizationDetails = components['schemas']['Customer-AmortizationDetails']

const props = defineProps<{
  /** Commit POST in flight (owned by the dialog shell). */
  loading?: boolean
}>()

const emit = defineEmits<{
  back: []
  confirm: [payload: { body: AmortizationCommitBody; date: Date }]
}>()

const { t } = useI18n()
const dialogsStore = useDialogsStore()

const isMulti = computed(() => (dialogsStore.amortization?.multiple?.documents.length ?? 0) > 0)

/** The documents the plan is created for: the single-mode document, or the Select-step picks. */
const selectedDocuments = computed<SettlementDocument[]>(() => {
  const payload = dialogsStore.amortization
  if (payload == null) return []
  const multiple = payload.multiple
  if (multiple != null && multiple.documents.length > 0) {
    return multiple.documents.filter((document) => multiple.selected.includes(document._id))
  }
  return payload.document != null ? [payload.document] : []
})
const firstDocument = computed(() => selectedDocuments.value[0])

const creditorName = computed(() => firstDocument.value?.creditor_name ?? '')
const currency = computed(() => firstDocument.value?.currency ?? 'SEK')

const numberOfMonths = ref(1)
const details = ref<AmortizationDetails | null>(null)
/** The month count `details` was calculated for — the re-seed detector below compares against it. */
const detailsMonths = ref<number | null>(null)

// Max advices: direct param, or interval lookup against the preview's total_debt.
// ⚠️ Legacy crashed when no interval matched (`matched_interval.max_number_of_advices` on
// undefined — e.g. before the first preview response); guarded here with a fallback
// (documented robustness fix, not a behavior change).
const maxNumberOfAdvices = computed(() => {
  const params = dialogsStore.amortization?.params
  if (params == null) return 0
  const intervals = params.max_number_of_advices_intervals
  if (intervals != null && intervals.length > 0) {
    const totalDebt = details.value?.total_debt
    const matched =
      totalDebt == null ? undefined : intervals.find((x) => x.from_amount <= totalDebt && totalDebt <= x.to_amount)
    return matched?.max_number_of_advices ?? params.max_number_of_advices ?? 1
  }
  return params.max_number_of_advices ?? 0
})

// EXACT legacy formula: last day of this month while today is at least
// advice_date_days_before_due_date days before month end, else last day of next month.
// Legacy round-tripped through 'yyyy-MM-dd' before toUTCString() — i.e. UTC midnight of the
// calendar day — mirrored via Date.UTC (a bare toUTCString() on a local-midnight date would
// shift the day west of UTC).
const firstPaymentDate = computed(() => {
  const today = new Date()
  const daysBefore = dialogsStore.amortization?.params?.advice_date_days_before_due_date ?? 0
  const base =
    today.getDate() <= getDaysInMonth(today) - daysBefore
      ? lastDayOfMonth(today)
      : lastDayOfMonth(add(today, { months: 1 }))
  return new Date(Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()))
})

const body = computed<AmortizationCommitBody>(() => {
  const cases: { id: string }[] = []
  const debtCollections: { id: string }[] = []
  for (const document of selectedDocuments.value) {
    if (document.reference === 'case') cases.push({ id: document._id })
    else if (document.reference === 'debt_collection') debtCollections.push({ id: document._id })
  }
  return {
    amortization_date: firstPaymentDate.value.toUTCString(),
    number_of_advices: numberOfMonths.value,
    cases,
    debt_collections: debtCollections,
    enforcements: [],
    send_first_advice_letter_only: false,
    skip_amortization_setup_fee: false,
  }
})

// Read-like calculation POST: it mutates nothing, so no invalidation and no success toast.
const detailsMutation = useMutation('post', '/customer--amortization/details-new-amortization', {
  meta: { notifyOnSuccess: false, skipAutoInvalidation: true },
})

// A failed FIRST preview leaves no numbers to show — tracked so the loading gates below release
// (a permanent spinner would strand the dialog) and the cost slot offers a retry instead.
const previewFailed = ref(false)

// Refresh the cost preview when the plan changes. Debounced so dragging the slider fires ONE
// request (not one per step); a latest-wins guard still protects against out-of-order responses.
let latestRequestId = 0
async function loadPreview(value: AmortizationCommitBody) {
  if ((value.cases.length === 0 && value.debt_collections.length === 0) || value.number_of_advices <= 0) {
    details.value = null
    return
  }
  const requestId = ++latestRequestId
  try {
    const response = await detailsMutation.mutateAsync({
      // @ts-expect-error Swagger lies — the details calculation requires the plan body.
      body: value,
    })
    if (requestId !== latestRequestId) return
    details.value = response
    detailsMonths.value = value.number_of_advices
    previewFailed.value = false
  } catch {
    // Global mutation handler snackbars the failure; keep the last known preview.
    if (requestId === latestRequestId) previewFailed.value = true
  }
}

watchDebounced(
  body,
  (value) => {
    void loadPreview(value)
  },
  { debounce: 300, immediate: true }
)

const previewLoading = computed(() => detailsMutation.isPending.value)

// Legacy parity: start the slider at the max. For interval-configs the real max needs the
// FIRST preview's total_debt (the lookup falls back low until then) — keep re-seeding as the
// max resolves, but stop the moment the user moves the slider themselves.
const monthsTouched = ref(false)
watch(
  maxNumberOfAdvices,
  (max) => {
    if (!monthsTouched.value && max > 0) numberOfMonths.value = max
  },
  { immediate: true }
)

// An interval-config knows its real max only from the first preview's total_debt, so the dialog
// opens on the fallback (1) and re-seeds once that response lands. Neither the fallback max nor
// the 1-month cost it produced may reach the user: the month row skeletons until the max is
// settled, and the cost keeps loading until the preview MATCHES the seeded month count.
// Both gates are pre-interaction only — after a manual slider change the previous amount stays
// put and updates in place (Anton 2026-08-03), no spinner flicker. Both also release on a failed
// preview: waiting for a response that will never come would strand the dialog (PR #895 review).
const maxPending = computed(
  () =>
    (dialogsStore.amortization?.params?.max_number_of_advices_intervals?.length ?? 0) > 0 &&
    details.value == null &&
    !previewFailed.value
)
const costPending = computed(
  () =>
    !previewFailed.value &&
    (details.value == null ||
      (!monthsTouched.value && detailsMonths.value != null && detailsMonths.value !== numberOfMonths.value))
)

function retryPreview() {
  previewFailed.value = false
  void loadPreview(body.value)
}

function decrement() {
  monthsTouched.value = true
  if (numberOfMonths.value > 1) numberOfMonths.value--
}
function increment() {
  monthsTouched.value = true
  if (numberOfMonths.value < maxNumberOfAdvices.value) numberOfMonths.value++
}

/** Present entries of a fee/debt record (only keys the API included). */
function debtEntries(record: Record<string, number | undefined> | undefined): [string, number][] {
  return Object.entries(record ?? {}).filter((entry): entry is [string, number] => entry[1] != null)
}
const simulatedEntries = computed(() => debtEntries(details.value?.simulated_additional_debt))
const transferableEntries = computed(() => debtEntries(details.value?.transferable_additional_debt_on_create))

function sumOf(entries: [string, number][]): number {
  let total = 0
  for (const [, amount] of entries) total += amount
  return total
}

const debtsCountLabel = computed(() =>
  isMulti.value ? `${t('common.debts')} (${selectedDocuments.value.length})` : t('common.debt')
)

function startAmortization() {
  emit('confirm', { body: body.value, date: firstPaymentDate.value })
}
</script>

<template>
  <v-sheet class="d-flex flex-column">
    <amortization-back-toolbar
      v-if="isMulti"
      page="setup"
      @back="emit('back')"
    />

    <div class="overflow-y-auto">
      <section class="px-4 pb-10">
        <div class="text-body-large font-weight-bold mb-2">{{ t('common.select_number_of_months') }}</div>
        <v-skeleton-loader
          v-if="maxPending"
          type="text"
          width="260"
        />
        <div
          v-else
          class="text-body-medium text-medium-emphasis"
        >
          {{ t('common.creditor_allows_amortization_x_months', [creditorName, maxNumberOfAdvices]) }}
        </div>
      </section>

      <section>
        <v-slider
          v-model="numberOfMonths"
          :disabled="maxPending"
          :max="maxNumberOfAdvices"
          :min="1"
          :step="1"
          thumb-label="always"
          @update:model-value="monthsTouched = true"
        >
          <template #prepend>
            <v-btn
              :icon="mdiMinus"
              :aria-label="t('common.previous')"
              size="small"
              variant="text"
              @click="decrement"
            />
          </template>
          <template #append>
            <v-btn
              :icon="mdiPlus"
              :aria-label="t('common.next')"
              size="small"
              variant="text"
              @click="increment"
            />
          </template>
        </v-slider>
      </section>

      <section class="px-4">
        <div class="d-flex align-center justify-space-between">
          <div class="text-body-large font-weight-bold">{{ t('common.monthly_cost') }}</div>
          <!-- Spinner on the FIRST load (no preview yet) and while an interval-config's max is
               still seeding. On manual slider changes the previous amount stays put and updates
               in place — no spinner flicker (Anton 2026-08-03). -->
          <v-progress-circular
            v-if="costPending"
            :size="36"
            indeterminate
          />
          <v-btn
            v-else-if="previewFailed && details == null"
            :loading="previewLoading"
            size="small"
            variant="text"
            @click="retryPreview"
          >
            {{ t('common.try_again') }}
          </v-btn>
          <div
            v-else
            class="text-headline-large"
          >
            {{ formatCurrency(details?.amount_per_advice, currency) }}
          </div>
        </div>
        <div class="text-body-small text-medium-emphasis text-right mb-2">
          {{ t('common.monthly_cost_footnote') }}
        </div>
      </section>

      <section class="px-4 flex-grow-1">
        <!-- ADA-4472 B: the breakdown is no longer behind a "Hur har vi räknat?" expander and
             the ADA-4433 fee card is gone — the sum rows render directly (Skulder / Tillkommande
             kostnader och ränta / Överförda kostnader / Total, and the visible rows always add up
             to the total), with the per-type fee rows behind a chevron on their sum row. Skeleton
             while the preview is pending; absent (not just empty) when a first preview failed —
             the retry lives in the cost slot above.

             Every row READS a figure the API already computed; nothing is derived here — the sum
             rows sum the API's own per-type record, i.e. the exact figures their expansion shows.
             In particular `advice_fee * number_of_advices` is NOT the advice fee total: the API
             simulates month by month and the plan needs one more advice than it sells to absorb
             the interest, which the monthly amount never budgeted for. That extra fee is inside
             `total_amortization_amount`, so it is charged — deriving the total here would have
             under-reported it (the 720 vs 780 lesson, ADA-4433). -->
        <div class="text-body-large font-weight-bold mb-4">{{ t('common.how_did_we_count') }}</div>

        <v-skeleton-loader
          v-if="costPending"
          type="paragraph"
        />

        <template v-else-if="details != null">
          <amortization-text-row
            :label="debtsCountLabel"
            :value="formatCurrency(details?.total_debt, currency)"
          />

          <amortization-sum-row
            v-if="simulatedEntries.length > 0"
            :label="t('common.additional_costs_and_interest')"
            :amount="formatCurrency(sumOf(simulatedEntries), currency)"
          >
            <amortization-text-row
              v-for="[transactionType, amount] in simulatedEntries"
              :key="`simulated_${transactionType}`"
              :label="t(`transactions.${transactionType}`)"
              :value="formatCurrency(amount, currency)"
            />
          </amortization-sum-row>

          <amortization-sum-row
            v-if="transferableEntries.length > 0"
            :label="t('common.transferred_costs')"
            :amount="formatCurrency(sumOf(transferableEntries), currency)"
          >
            <amortization-text-row
              v-for="[transactionType, amount] in transferableEntries"
              :key="`transferable_${transactionType}`"
              :label="t(`transactions.${transactionType}`)"
              :value="formatCurrency(amount, currency)"
            />
          </amortization-sum-row>

          <div class="d-flex justify-space-between mb-4">
            <div class="text-body-large font-weight-bold">{{ t('common.total') }}</div>
            <div class="text-body-large font-weight-bold tabular-nums">
              {{ formatCurrency(details?.total_amortization_amount, currency) }}
            </div>
          </div>
        </template>

        <div class="text-body-large font-weight-bold mb-4">{{ t('common.amortization_information') }}</div>
        <amortization-text-row
          :label="t('common.first_payment_date')"
          :value="formatDate(firstPaymentDate)"
        />
        <div class="text-body-medium">
          {{ t('common.advices_information') }}
        </div>
      </section>
    </div>

    <v-btn
      :disabled="previewLoading || costPending || details == null || props.loading"
      :loading="props.loading"
      class="mx-4 mt-4"
      color="primary"
      @click="startAmortization"
    >
      {{ t('common.split') }}
    </v-btn>
    <!-- The acknowledgement text (legacy parity). Its amounts live in the sum rows above since
         ADA-4472 removed the ADA-4433 fee card. -->
    <div class="ma-4 text-body-small text-medium-emphasis">
      {{ t('common.amortization_start_information') }}
    </div>
  </v-sheet>
</template>
